/**
 * ScrollInView — the SINGLE reusable "smooth-scroll a target into view, gated
 * against user-scroll, abortable, axis-parameterized" mechanism. BOTH the
 * horizontal column-track peek scroll (ColumnTrack) AND the new vertical
 * bottom-image scroll (DropdownView / ButtonColumnView dropdown mode) consume it,
 * so the arbiter-gating + freeze-abort + smooth-write live in exactly one place.
 *
 * It does NOT own a ScrollIntentArbiter — one is injected, so each scroll context
 * (a track, the window, a modal scroll body) keeps its own intent state. It does
 * NOT bind any user-scroll listeners; the caller feeds the injected arbiter from
 * its own sources (ColumnTrack already does this device-independently, D31/D34;
 * the vertical caller wires a passive scroll/wheel/touchstart/keydown →
 * arbiter.notifyUserScroll on the relevant scroll context).
 *
 * **Scroll context = the SCROLLER, not always the window.** A `container` of
 * `null` means the window/document is the scroller (the standalone page + SSR
 * surface scroll the document); a non-null `container` is an inner scroll element
 * (the catalog modal scrolls its `.modal-body`, not the window — verified against
 * `_mf-page-modal.scss`). The caller resolves which via `nearestScrollContainer`,
 * so this mechanism never hardcodes a surface.
 *
 * @see view/scroll-intent-arbiter.ts (the injected gate)
 * @see view/rect-visibility.ts (the out-of-view gate, pure)
 * @see view/scroll-target.ts (the reveal-offset math, pure)
 * @see view/peek-scroll.ts (horizontal peek target — caller computes, passes via requestToOffset)
 * @see docs/speed-optimization/research/2026-06-07 - model-finder-column-plan-and-modal/research/dropdown-bottom-image-autoscroll.md §3.3
 */

import { ScrollIntentArbiter } from './scroll-intent-arbiter';
import { type Axis, extentAlong, isFullyVisibleAlong } from './rect-visibility';
import { scrollOffsetToReveal } from './scroll-target';

/** The smooth-animation settle window (ms) — mirrors ColumnTrack's 600ms today. */
const SETTLE_MS = 600;

export interface ScrollInViewOptions {
  /** The injected user-scroll gate (one per scroll context). */
  arbiter: ScrollIntentArbiter;
  /** Injected for testing; defaults to window. */
  win?: Window;
}

/** A request to reveal an ELEMENT (the mechanism reads its rect + computes the offset). */
export interface RevealRequest {
  axis: Axis;
  /** The element to bring into view. */
  target: HTMLElement;
  /** The scroll container; null ⇒ the window/document viewport. */
  container: HTMLElement | null;
  /** Margin between the revealed edge and the viewport edge (px). Default 0. */
  margin?: number;
  /** When false, skip the out-of-view gate and always scroll (horizontal peek). Default true. */
  gateOutOfView?: boolean;
  /**
   * Alignment policy passed to `scrollOffsetToReveal`. Default `'reveal'` (minimal
   * scroll into view). `'center'` parks the target's centre at the viewport centre
   * and implies NO out-of-view gate — centring is the goal even for an already-
   * visible-but-off-centre target (e.g. the modal-open reveal of a mid-list model).
   */
  align?: 'reveal' | 'center';
}

/** A request to scroll a SCROLLER to a pre-computed OFFSET (horizontal peek path). */
export interface OffsetRequest {
  axis: Axis;
  scroller: HTMLElement;
  offset: number;
}

export class ScrollInView {
  private readonly arbiter: ScrollIntentArbiter;
  private readonly win: Window;
  /** True while OUR smooth glide runs, so the caller can suppress self-emitted scroll events. */
  private programmatic = false;

  constructor(options: ScrollInViewOptions) {
    this.arbiter = options.arbiter;
    this.win = options.win ?? window;
  }

  /** Is our own programmatic glide currently running? (caller gates its scroll listener on this). */
  get isProgrammatic(): boolean {
    return this.programmatic;
  }

  /**
   * Reveal an element along `axis` if it is not already fully visible (unless
   * gateOutOfView === false). Arbiter-gated; registers a freeze-abort; settles
   * after the smooth window.
   */
  request(req: RevealRequest): void {
    // `center` always re-aligns (centring an off-centre but visible target is the
    // point), so it bypasses the out-of-view gate regardless of gateOutOfView.
    const gate = (req.gateOutOfView ?? true) && req.align !== 'center';
    const viewport = this.viewportExtent(req.axis, req.container);
    const targetRect = req.target.getBoundingClientRect();
    const targetExtent = extentAlong(req.axis, targetRect);
    if (gate && isFullyVisibleAlong(targetExtent, viewport)) return; // already visible → no scroll

    const current = this.currentOffset(req.axis, req.container);
    const viewportLength = viewport.end - viewport.start;
    // Translate the viewport-relative target start into a content-relative start.
    const targetStart = current + (targetExtent.start - viewport.start);
    const offset = scrollOffsetToReveal({
      current,
      targetStart,
      targetLength: targetExtent.end - targetExtent.start,
      viewportLength,
      maxScroll: this.maxScroll(req.axis, req.container),
      margin: req.margin ?? 0,
      align: req.align ?? 'reveal',
    });
    this.scheduleSmooth(req.axis, req.container, offset);
  }

  /** Scroll a scroller to a pre-computed offset (the horizontal peek path — always applied). */
  requestToOffset(req: OffsetRequest): void {
    this.scheduleSmooth(req.axis, req.scroller, req.offset);
  }

  // -- internals: arbiter-gated smooth write + freeze-abort + settle -----------

  private scheduleSmooth(axis: Axis, container: HTMLElement | null, offset: number): void {
    this.arbiter.requestAutoScroll(() => this.smoothScroll(axis, container, offset));
  }

  private smoothScroll(axis: Axis, container: HTMLElement | null, offset: number): void {
    this.programmatic = true;
    this.writeSmooth(axis, container, offset);
    // Abort = freeze at the live offset (an instant scroll there interrupts the glide).
    this.arbiter.registerAbort(() => this.freeze(axis, container));
    // Let the flag + abort handle expire once the smooth animation has had time to
    // settle, so a later user scroll is recorded as fresh intent, not an "abort".
    this.win.setTimeout(() => {
      this.programmatic = false;
      this.arbiter.settle();
    }, SETTLE_MS);
  }

  private writeSmooth(axis: Axis, container: HTMLElement | null, offset: number): void {
    (container ?? this.win).scrollTo(this.scrollOptions(axis, offset, this.behavior()));
  }

  /** Abort = freeze at the live offset (an instant scroll there interrupts the smooth glide). */
  private freeze(axis: Axis, container: HTMLElement | null): void {
    const here = this.currentOffset(axis, container);
    (container ?? this.win).scrollTo(this.scrollOptions(axis, here, 'auto'));
  }

  /** Build the axis-correct ScrollToOptions (left for x, top for y). */
  private scrollOptions(axis: Axis, offset: number, behavior: ScrollBehavior): ScrollToOptions {
    return axis === 'y' ? { top: offset, behavior } : { left: offset, behavior };
  }

  /** Respect reduced-motion: an instant jump instead of a smooth glide (Info Gap §11.6). */
  private behavior(): ScrollBehavior {
    return this.win.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  private currentOffset(axis: Axis, container: HTMLElement | null): number {
    if (container) return axis === 'y' ? container.scrollTop : container.scrollLeft;
    return axis === 'y' ? this.win.scrollY : this.win.scrollX;
  }

  private maxScroll(axis: Axis, container: HTMLElement | null): number {
    if (container) {
      return axis === 'y'
        ? container.scrollHeight - container.clientHeight
        : container.scrollWidth - container.clientWidth;
    }
    const doc = this.win.document.documentElement;
    return axis === 'y' ? doc.scrollHeight - this.win.innerHeight : doc.scrollWidth - this.win.innerWidth;
  }

  private viewportExtent(axis: Axis, container: HTMLElement | null): { start: number; end: number } {
    if (container) {
      return extentAlong(axis, container.getBoundingClientRect());
    }
    return axis === 'y' ? { start: 0, end: this.win.innerHeight } : { start: 0, end: this.win.innerWidth };
  }
}

/**
 * The nearest scrollable ancestor of `el` along the Y axis, or `null` if none
 * (⇒ the window/document scrolls). Keeps `ScrollInView` callers surface-agnostic:
 * the catalog modal resolves its `.modal-body` (overflow-y:auto), the standalone
 * page + SSR resolve `null` (the document scrolls). Walks the ancestor chain and
 * stops at the first element whose computed `overflow-y` is `auto`/`scroll` AND
 * which actually overflows; bails at `<body>`/`<html>` (the document scroller).
 */
export function nearestScrollContainer(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const overflowY = getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}
